import { formatProDocCode, PRO_COMMANDE_SEQ, type CommandeStatus } from '@erp/domain/pro';
import type { TypedSupabaseClient } from '../client';
import type {
  CreateProCommandeInput,
  IProCommandeRepository,
  ProCommande,
  ProCommandeFilters,
  ProCommandeLine,
  ProStatistics,
  SalesDocument,
  UpdateProCommandeInput,
} from '../../../interfaces/src';
import { SupabaseSalesRepository } from './sales-repository';

export class SupabaseProCommandeRepository implements IProCommandeRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: ProCommandeFilters): Promise<ProCommande[]> {
    let query = (this.supabase as any)
      .from('pro_commandes')
      .select('*, lines:pro_commande_lines(*), client:pro_clients(*)');
    if (filters?.status) query = query.eq('status', filters.status);
    if (filters?.from) query = query.gte('date', filters.from);
    if (filters?.to) query = query.lte('date', filters.to);
    if (filters?.search) query = query.ilike('code', `%${filters.search}%`);
    const { data, error } = await query.order('date', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as Array<Record<string, unknown>>).map(mapCommande);
  }

  async getByCode(code: string): Promise<ProCommande | null> {
    const { data, error } = await (this.supabase as any)
      .from('pro_commandes')
      .select('*, lines:pro_commande_lines(*), client:pro_clients(*)')
      .eq('code', code)
      .maybeSingle();
    if (error) {
      if (error.code === 'PGRST116') return null;
      throw error;
    }
    if (!data) return null;
    const order = mapCommande(data);
    if (order.deliverySalesDocumentId) return order;

    const linkedDelivery = await this.getLinkedDeliveryByCommandeCode(code);
    if (!linkedDelivery) return order;
    await this.linkDeliveryToCommande(code, linkedDelivery.id);
    return { ...order, deliverySalesDocumentId: linkedDelivery.id };
  }

  async createDeliveryFromCommande(code: string): Promise<SalesDocument> {
    const order = await this.getByCode(code);
    if (!order) throw new Error('Commande introuvable');
    const salesRepository = new SupabaseSalesRepository(this.supabase);

    if (order.deliverySalesDocumentId) {
      const existing = await salesRepository.getDocumentById(order.deliverySalesDocumentId);
      if (existing) return existing;
    }

    const linkedByCode = await this.getLinkedDeliveryByCommandeCode(code);
    if (linkedByCode) {
      await this.linkDeliveryToCommande(code, linkedByCode.id);
      return linkedByCode;
    }

    const articleMap = await this.getArticleMap(order.lines?.map((l) => l.articleId) ?? []);
    const totalDiscount = (order.lines ?? []).reduce((sum, line) => sum + line.montantRemise, 0);
    let delivery: SalesDocument;
    try {
      delivery = await salesRepository.createDocument({
        docType: 'delivery',
        customerSource: 'pro',
        clientId: null,
        proCustomerId: order.proCustomerId,
        proClientId: order.proClientId,
        docDate: order.date,
        notes: order.notes ?? undefined,
        totalHt: order.pht,
        totalDiscount,
        totalVat: order.tva,
        totalTtc: order.pttc,
        deliveryFee: order.deliveryFee,
        netToPay: order.totalAPayer,
        proCommandeCode: order.code,
        lines: (order.lines ?? []).map((line) => {
          const article = articleMap.get(line.articleId);
          const quantity = Math.max(line.quantite, 0);
          return {
            articleId: line.articleId,
            description: article?.name ?? line.articleId,
            quantity: line.quantite,
            unitPriceHt: grossUnitPriceHt(line.pht, quantity, line.remise),
            purchasePrice: article?.purchasePriceHt ?? 0,
            discountPct: line.remise,
            vatRate: line.tva,
            totalHt: line.pht,
            totalTtc: line.pttc,
          };
        }),
      });
    } catch (error) {
      const recovered = await this.getLinkedDeliveryByCommandeCode(code);
      if (!recovered) throw error;
      await this.linkDeliveryToCommande(code, recovered.id);
      return recovered;
    }

    await this.linkDeliveryToCommande(code, delivery.id);
    return delivery;
  }

  private async linkDeliveryToCommande(code: string, deliveryId: string): Promise<void> {
    const { error } = await (this.supabase as any)
      .from('pro_commandes')
      .update({ delivery_sales_document_id: deliveryId })
      .eq('code', code);
    if (error) throw error;
  }

  private async getLinkedDeliveryByCommandeCode(code: string): Promise<SalesDocument | null> {
    const { data, error } = await (this.supabase as any)
      .from('sales_documents')
      .select('id')
      .eq('doc_type', 'delivery')
      .eq('pro_commande_code', code)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    const id = (data?.id as string | undefined) ?? null;
    if (!id) return null;
    return new SupabaseSalesRepository(this.supabase).getDocumentById(id);
  }

  async create(input: CreateProCommandeInput): Promise<ProCommande> {
    const code = await this.getNextCode(new Date().getFullYear());
    const { error } = await (this.supabase as any).rpc('pro_create_commande_atomic', {
      p_code: code,
      p_pro_client_id: input.proClientId ?? null,
      p_parent_devis_code: input.parentDevisCode ?? null,
      p_status: 'en_attente',
      p_pht: input.pht,
      p_pttc: input.pttc,
      p_tva: input.tva,
      p_taux_remise: input.tauxRemise,
      p_type_remise: input.typeRemise,
      p_total_commission: input.totalCommission,
      p_delivery_fee: input.deliveryFee,
      p_total_used: input.totalUsed,
      p_total_a_payer: input.totalAPayer,
      p_used_solde: input.usedSolde,
      p_delivery_address: input.deliveryAddress ?? null,
      p_delivery_city: input.deliveryCity ?? null,
      p_delivery_postal_code: input.deliveryPostalCode ?? null,
      p_delivery_complement: input.deliveryComplement ?? null,
      p_lines: input.lines.map((l) => ({
        article_id: l.articleId,
        quantite: l.quantite,
        pht: l.pht,
        pttc: l.pttc,
        tva: l.tva,
        remise: l.remise,
        montant_remise: l.montantRemise,
      })),
    });
    if (error) throw error;
    const created = await this.getByCode(code);
    if (!created) throw new Error('Commande créée mais introuvable');
    return created;
  }

  async updateStatus(code: string, status: CommandeStatus): Promise<ProCommande> {
    const { error } = await (this.supabase as any)
      .from('pro_commandes')
      .update({ status })
      .eq('code', code);
    if (error) throw error;
    const fresh = await this.getByCode(code);
    if (!fresh) throw new Error('Commande introuvable');
    return fresh;
  }

  async update(code: string, input: UpdateProCommandeInput): Promise<ProCommande> {
    const existing = await this.getByCode(code);
    if (!existing) throw new Error('Commande introuvable');

    const { error: updateError } = await (this.supabase as any)
      .from('pro_commandes')
      .update({
        pro_client_id: input.proClientId ?? null,
        pht: input.pht,
        pttc: input.pttc,
        tva: input.tva,
        taux_remise: input.tauxRemise,
        type_remise: input.typeRemise,
        total_commission: input.totalCommission,
        delivery_fee: input.deliveryFee,
        total_used: input.totalUsed,
        total_a_payer: input.totalAPayer,
        used_solde: input.usedSolde,
        delivery_address: input.deliveryAddress ?? null,
        delivery_city: input.deliveryCity ?? null,
        delivery_postal_code: input.deliveryPostalCode ?? null,
        delivery_complement: input.deliveryComplement ?? null,
        notes: input.notes ?? null,
      })
      .eq('code', code);
    if (updateError) throw updateError;

    const { error: deleteError } = await (this.supabase as any)
      .from('pro_commande_lines')
      .delete()
      .eq('commande_code', code);
    if (deleteError) throw deleteError;

    if (input.lines.length > 0) {
      const { error: insertError } = await (this.supabase as any)
        .from('pro_commande_lines')
        .insert(input.lines.map((line, idx) => ({
          tenant_id: existing.tenantId,
          commande_code: code,
          article_id: line.articleId,
          quantite: line.quantite,
          pht: line.pht,
          pttc: line.pttc,
          tva: line.tva,
          remise: line.remise,
          montant_remise: line.montantRemise,
          line_number: idx + 1,
        })));
      if (insertError) throw insertError;
    }

    const linkedDeliveryId = existing.deliverySalesDocumentId
      ?? (await this.getLinkedDeliveryByCommandeCode(code))?.id
      ?? null;
    if (linkedDeliveryId) {
      if (!existing.deliverySalesDocumentId) {
        await this.linkDeliveryToCommande(code, linkedDeliveryId);
      }
      await this.updateLinkedDelivery(linkedDeliveryId, code, existing.proCustomerId, input);
    }

    const fresh = await this.getByCode(code);
    if (!fresh) throw new Error('Commande introuvable');
    return fresh;
  }

  async getNextCode(year: number): Promise<string> {
    const { data, error } = await (this.supabase as any).rpc('get_next_pro_sequence_value', {
      p_seq_name: PRO_COMMANDE_SEQ,
    });
    if (error) throw error;
    return formatProDocCode('CMD', year, Number(data));
  }

  async getStatistics(): Promise<ProStatistics> {
    const { data: cmds, error } = await (this.supabase as any)
      .from('pro_commandes')
      .select('status, pttc, total_commission, date');
    if (error) throw error;
    const { count: devisCount } = await (this.supabase as any)
      .from('pro_devis')
      .select('*', { count: 'exact', head: true });

    const rows = (cmds ?? []) as Array<Record<string, unknown>>;
    const totals: { totalCommandes: number; totalRevenue: number; totalCommission: number; pendingCommandes: number } = {
      totalCommandes: 0,
      totalRevenue: 0,
      totalCommission: 0,
      pendingCommandes: 0,
    };
    for (const r of rows) {
      totals.totalCommandes += 1;
      totals.totalRevenue += Number(r['pttc'] ?? 0);
      totals.totalCommission += Number(r['total_commission'] ?? 0);
      if (r['status'] === 'en_attente') totals.pendingCommandes += 1;
    }

    // Group by month for the chart (last 6 months)
    const buckets = new Map<string, { count: number; revenue: number }>();
    for (const r of rows) {
      const date = r['date'] as string;
      if (!date) continue;
      const month = date.slice(0, 7); // YYYY-MM
      const existing = buckets.get(month) ?? { count: 0, revenue: 0 };
      existing.count += 1;
      existing.revenue += Number(r['pttc'] ?? 0);
      buckets.set(month, existing);
    }
    const monthlyCommandes = [...buckets.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6)
      .map(([month, v]) => ({ month, count: v.count, revenue: v.revenue }));

    return {
      totalCommandes: totals.totalCommandes,
      totalDevis: devisCount ?? 0,
      totalRevenue: totals.totalRevenue,
      totalCommission: totals.totalCommission,
      pendingCommandes: totals.pendingCommandes,
      monthlyCommandes,
    };
  }

  private async getArticleMap(articleIds: string[]): Promise<Map<string, ArticleRow>> {
    const ids = [...new Set(articleIds.filter(Boolean))];
    if (ids.length === 0) return new Map();
    const { data, error } = await (this.supabase as any)
      .from('articles')
      .select('id, name, purchase_price_ht')
      .in('id', ids);
    if (error) throw error;
    return new Map(
      ((data ?? []) as Array<Record<string, unknown>>).map((row) => [
        row['id'] as string,
        {
          name: row['name'] as string,
          purchasePriceHt: Number(row['purchase_price_ht'] ?? 0),
        },
      ]),
    );
  }

  private async updateLinkedDelivery(
    deliveryId: string,
    code: string,
    proCustomerId: string,
    input: UpdateProCommandeInput,
  ): Promise<void> {
    const articleMap = await this.getArticleMap(input.lines.map((l) => l.articleId));
    const totalDiscount = input.lines.reduce((sum, line) => sum + line.montantRemise, 0);
    const { error: docError } = await (this.supabase as any)
      .from('sales_documents')
      .update({
        notes: input.notes ?? null,
        total_ht: input.pht,
        total_discount: totalDiscount,
        total_vat: input.tva,
        total_ttc: input.pttc,
        delivery_fee: input.deliveryFee,
        net_to_pay: input.totalAPayer,
        pro_commande_code: code,
        customer_source: 'pro',
        client_id: null,
        online_customer_id: null,
        pro_customer_id: proCustomerId,
        pro_client_id: input.proClientId ?? null,
      })
      .eq('id', deliveryId);
    if (docError) throw docError;

    const { error: deleteError } = await (this.supabase as any)
      .from('sales_document_lines')
      .delete()
      .eq('document_id', deliveryId);
    if (deleteError) throw deleteError;

    if (input.lines.length > 0) {
      const { error: insertError } = await (this.supabase as any)
        .from('sales_document_lines')
        .insert(input.lines.map((line, idx) => {
          const article = articleMap.get(line.articleId);
          const quantity = Math.max(line.quantite, 0);
          return {
            document_id: deliveryId,
            line_number: idx + 1,
            article_id: line.articleId,
            description: article?.name ?? line.articleId,
            quantity: line.quantite,
            unit_price_ht: grossUnitPriceHt(line.pht, quantity, line.remise),
            purchase_price: article?.purchasePriceHt ?? 0,
            discount_pct: line.remise,
            vat_rate: line.tva,
            total_ht: line.pht,
            total_ttc: line.pttc,
          };
        }));
      if (insertError) throw insertError;
    }
  }
}

interface ArticleRow {
  name: string;
  purchasePriceHt: number;
}

function round3(value: number): number {
  return Math.round((value + Number.EPSILON) * 1000) / 1000;
}

function grossUnitPriceHt(totalHtAfterDiscount: number, quantity: number, discountPct: number): number {
  if (quantity <= 0) return totalHtAfterDiscount;
  const discountFactor = 1 - discountPct / 100;
  if (discountFactor <= 0) return round3(totalHtAfterDiscount / quantity);
  return round3(totalHtAfterDiscount / discountFactor / quantity);
}

function mapLine(row: Record<string, unknown>): ProCommandeLine {
  return {
    id: row['id'] as string,
    commandeCode: row['commande_code'] as string,
    articleId: row['article_id'] as string,
    quantite: Number(row['quantite']),
    pht: Number(row['pht']),
    pttc: Number(row['pttc']),
    tva: Number(row['tva'] ?? 19),
    remise: Number(row['remise'] ?? 0),
    montantRemise: Number(row['montant_remise'] ?? 0),
    lineNumber: Number(row['line_number'] ?? 0),
  };
}

function mapCommande(row: Record<string, unknown>): ProCommande {
  const lines = (row['lines'] as Array<Record<string, unknown>> | undefined) ?? [];
  const client = row['client'] as Record<string, unknown> | null | undefined;
  return {
    code: row['code'] as string,
    tenantId: row['tenant_id'] as string,
    proCustomerId: row['pro_customer_id'] as string,
    proClientId: (row['pro_client_id'] as string) ?? null,
    parentDevisCode: (row['parent_devis_code'] as string) ?? null,
    date: row['date'] as string,
    status: row['status'] as ProCommande['status'],
    pht: Number(row['pht'] ?? 0),
    pttc: Number(row['pttc'] ?? 0),
    tva: Number(row['tva'] ?? 0),
    tauxRemise: Number(row['taux_remise'] ?? 0),
    typeRemise: row['type_remise'] as ProCommande['typeRemise'],
    totalCommission: Number(row['total_commission'] ?? 0),
    deliveryFee: Number(row['delivery_fee'] ?? 0),
    totalUsed: Number(row['total_used'] ?? 0),
    totalAPayer: Number(row['total_a_payer'] ?? 0),
    usedSolde: Boolean(row['used_solde']),
    deliveryAddress: (row['delivery_address'] as string) ?? null,
    deliveryCity: (row['delivery_city'] as string) ?? null,
    deliveryPostalCode: (row['delivery_postal_code'] as string) ?? null,
    deliveryComplement: (row['delivery_complement'] as string) ?? null,
    deliverySalesDocumentId: (row['delivery_sales_document_id'] as string) ?? null,
    notes: (row['notes'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    lines: lines.map(mapLine).sort((a, b) => a.lineNumber - b.lineNumber),
    client: client
      ? {
          id: client['id'] as string,
          tenantId: client['tenant_id'] as string,
          proCustomerId: client['pro_customer_id'] as string,
          nom: client['nom'] as string,
          prenom: (client['prenom'] as string) ?? null,
          email: (client['email'] as string) ?? null,
          telephone: (client['telephone'] as string) ?? null,
          adresse: (client['adresse'] as string) ?? null,
          ville: (client['ville'] as string) ?? null,
          cp: (client['cp'] as string) ?? null,
          complement: (client['complement'] as string) ?? null,
          depense: Number(client['depense'] ?? 0),
          commission: Number(client['commission'] ?? 0),
          soldeCumule: Number(client['solde_cumule'] ?? 0),
          notes: (client['notes'] as string) ?? null,
          createdAt: client['created_at'] as string,
          updatedAt: client['updated_at'] as string,
        }
      : null,
  };
}
